<template>
  <div class="audio-player">
    <!-- 顶部播放器 -->
    <div class="player-main">
      <!-- 封面 -->
      <div class="player-cover">
        <img v-if="cover" :src="cover" alt="cover" />
        <div v-else class="cover-placeholder">
          <a-icon type="customer-service" />
        </div>
      </div>

      <!-- 中间控制区 -->
      <div class="player-controls">
        <!-- 标题 -->
        <div class="player-title" :title="currentTrack ? currentTrack.title : ''">
          {{ currentTrack ? currentTrack.title : '未选择音频' }}
        </div>

        <!-- 进度条 -->
        <div class="player-progress">
          <span class="time">{{ formatTime(currentTime) }}</span>
          <a-slider
            class="progress-slider"
            :value="progressValue"
            :tip-formatter="null"
            :disabled="!currentTrack"
            @change="onProgressChange"
            @afterChange="onProgressAfterChange"
          />
          <span class="time">{{ formatTime(duration) }}</span>
        </div>

        <!-- 控制按钮 -->
        <div class="player-btns">
          <!-- 上一首 -->
          <a-icon
            type="step-backward"
            class="ctrl-btn"
            :class="{ disabled: !hasPrev }"
            @click="prev"
          />

          <!-- 播放/暂停 -->
          <div class="play-btn" @click="togglePlay">
            <a-icon :type="playing ? 'pause-circle' : 'play-circle'" />
          </div>

          <!-- 下一首 -->
          <a-icon
            type="step-forward"
            class="ctrl-btn"
            :class="{ disabled: !hasNext }"
            @click="next"
          />

          <!-- 音量 -->
          <a-popover placement="bottom" trigger="click" overlayClassName="volume-popover">
            <template slot="content">
              <div class="volume-ctrl">
                <a-slider vertical :value="volumeValue" @change="onVolumeChange" style="height:80px" />
              </div>
            </template>
            <a-icon
              :type="muted || volume === 0 ? 'sound' : 'sound'"
              class="ctrl-btn"
              :class="{ muted: muted || volume === 0 }"
              @click.stop
            />
          </a-popover>

          <!-- 播放速度 -->
          <a-dropdown :trigger="['click']" placement="bottomCenter">
            <span class="speed-btn">{{ currentSpeed }}x</span>
            <a-menu slot="overlay" @click="onSpeedChange">
              <a-menu-item v-for="s in speeds" :key="s">{{ s }}x</a-menu-item>
            </a-menu>
          </a-dropdown>

          <!-- 播放模式 -->
          <a-icon :type="playModeIcon" class="ctrl-btn" @click="togglePlayMode" />

          <!-- 收起/展开列表 -->
          <a-tooltip :title="listVisible ? '收起列表' : '展开列表'">
            <a-icon
              type="unordered-list"
              class="ctrl-btn"
              :class="{ active: listVisible }"
              @click="listVisible = !listVisible"
            />
          </a-tooltip>
        </div>
      </div>
    </div>

    <!-- 播放列表 -->
    <transition name="list-slide">
      <div v-show="listVisible" class="player-playlist">
        <div class="playlist-header">
          <span>播放列表</span>
          <span class="playlist-count">{{ playlist.length }} 首</span>
        </div>
        <div class="playlist-body">
          <div
            v-for="(track, index) in playlist"
            :key="track.id || index"
            class="playlist-item"
            :class="{ active: currentIndex === index }"
            @click="selectTrack(index)"
          >
            <span class="item-index">{{ index + 1 }}</span>
            <span class="item-title" :title="track.title">{{ track.title }}</span>
            <a-icon v-if="currentIndex === index && playing" type="sound" class="item-playing" />
          </div>
        </div>
      </div>
    </transition>

    <!-- 隐藏的 audio 元素 -->
    <audio
      ref="audio"
      :src="currentSrc"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @ended="onEnded"
      @canplay="onCanPlay"
    />
  </div>
</template>

<script>
export default {
  name: 'AudioPlayer',

  props: {
    // 播放列表，每项 { id, title, src, cover }
    playlist: {
      type: Array,
      default: () => [],
    },
    // 默认播放索引
    defaultIndex: {
      type: Number,
      default: 0,
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      currentIndex: this.defaultIndex,
      playing: false,
      currentTime: 0,
      duration: 0,
      volume: 80,
      muted: false,
      currentSpeed: 1,
      speeds: [1, 1.1, 1.2, 1.3, 1.4, 1.5, 2],
      // list: 顺序播放  single: 单曲循环
      playMode: 'list',
      listVisible: true,
      seeking: false,
    }
  },

  computed: {
    currentTrack() {
      return this.playlist[this.currentIndex] || null
    },
    currentSrc() {
      return this.currentTrack ? this.currentTrack.src : ''
    },
    cover() {
      return this.currentTrack ? this.currentTrack.cover : ''
    },
    progressValue() {
      if (!this.duration) return 0
      return Math.floor((this.currentTime / this.duration) * 100)
    },
    volumeValue() {
      return this.muted ? 0 : this.volume
    },
    hasPrev() {
      return this.playlist.length > 1
    },
    hasNext() {
      return this.playlist.length > 1
    },
    playModeIcon() {
      const icons = { list: 'retweet', single: 'swap' }
      return icons[this.playMode]
    },
    playModeLabel() {
      const labels = { list: '顺序播放', single: '单曲循环' }
      return labels[this.playMode]
    },
  },

  watch: {
    currentSrc(val) {
      if (val) {
        this.$nextTick(() => {
          const audio = this.$refs.audio
          audio.load()
          audio.playbackRate = this.currentSpeed
          if (this.autoplay || this.playing) {
            audio.play().then(() => { this.playing = true }).catch(() => {})
          }
        })
      }
    },
    playlist: {
      immediate: true,
      handler(val) {
        if (val.length && this.currentIndex >= val.length) {
          this.currentIndex = 0
        }
      },
    },
  },

  mounted() {
    const audio = this.$refs.audio
    audio.volume = this.volume / 100
    audio.playbackRate = this.currentSpeed
    if (this.autoplay && this.currentSrc) {
      audio.play().then(() => { this.playing = true }).catch(() => {})
    }
  },

  beforeDestroy() {
    const audio = this.$refs.audio
    if (audio) {
      audio.pause()
      audio.src = ''
    }
  },

  methods: {
    togglePlay() {
      if (!this.currentTrack) return
      const audio = this.$refs.audio
      if (this.playing) {
        audio.pause()
        this.playing = false
      } else {
        audio.play().then(() => { this.playing = true }).catch(() => {})
      }
    },

    prev() {
      if (!this.hasPrev) return
      this.currentIndex = (this.currentIndex - 1 + this.playlist.length) % this.playlist.length
      this.playing = true
    },

    next() {
      if (!this.hasNext) return
      this.currentIndex = (this.currentIndex + 1) % this.playlist.length
      this.playing = true
    },

    selectTrack(index) {
      if (this.currentIndex === index) {
        this.togglePlay()
      } else {
        this.currentIndex = index
        this.playing = true
      }
    },

    onProgressChange(val) {
      // 拖动时只更新显示，不跳转
      this.seeking = true
      this.currentTime = (val / 100) * this.duration
    },

    onProgressAfterChange(val) {
      const audio = this.$refs.audio
      audio.currentTime = (val / 100) * this.duration
      this.seeking = false
    },

    onTimeUpdate() {
      if (!this.seeking) {
        this.currentTime = this.$refs.audio.currentTime
      }
    },

    onLoadedMetadata() {
      this.duration = this.$refs.audio.duration || 0
    },

    onCanPlay() {
      this.duration = this.$refs.audio.duration || 0
    },

    onEnded() {
      this.$emit('ended', this.currentIndex)
      if (this.playMode === 'single') {
        const audio = this.$refs.audio
        audio.currentTime = 0
        audio.play().then(() => { this.playing = true }).catch(() => {})
      } else {
        this.next()
      }
    },

    onVolumeChange(val) {
      this.volume = val
      this.muted = val === 0
      this.$refs.audio.volume = val / 100
    },

    onSpeedChange({ key }) {
      this.currentSpeed = Number(key)
      this.$refs.audio.playbackRate = this.currentSpeed
    },

    togglePlayMode() {
      this.playMode = this.playMode === 'list' ? 'single' : 'list'
      this.$toast(this.playModeLabel)
    },

    formatTime(sec) {
      if (!sec || isNaN(sec)) return '00:00'
      const m = Math.floor(sec / 60)
      const s = Math.floor(sec % 60)
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },

    // 对外暴露的方法，方便父组件扩展
    play() { this.togglePlay() },
    pause() {
      this.$refs.audio.pause()
      this.playing = false
    },
    stop() {
      const audio = this.$refs.audio
      audio.pause()
      audio.currentTime = 0
      this.playing = false
    },
  },
}
</script>

<style lang="less" scoped>
.audio-player {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  user-select: none;
}

// -------- 主播放区 --------
.player-main {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
}

.player-cover {
  flex-shrink: 0;
  width: 56px;
  height: 80px;
  border-radius: 6px;
  overflow: hidden;
  background: #f0f2f5;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .cover-placeholder {
    font-size: 28px;
    color: #bbb;
  }
}

.player-controls {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.player-title {
  font-size: 14px;
  font-weight: 500;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// -------- 进度条 --------
.player-progress {
  display: flex;
  align-items: center;
  gap: 8px;

  .time {
    font-size: 12px;
    color: #999;
    flex-shrink: 0;
    width: 38px;
    text-align: center;
  }

  .progress-slider {
    flex: 1;
    margin: 0;
  }
}

// -------- 控制按钮 --------
.player-btns {
  display: flex;
  align-items: center;
  gap: 16px;

  .ctrl-btn {
    font-size: 18px;
    color: #555;
    cursor: pointer;
    transition: color 0.2s;

    &:hover {
      color: #09C0D7;
    }

    &.disabled {
      color: #ccc;
      cursor: not-allowed;
    }

    &.active,
    &.muted {
      color: #09C0D7;
    }
  }

  .play-btn {
    font-size: 28px;
    color: #09C0D7;
    cursor: pointer;
    line-height: 1;
    transition: transform 0.15s;

    &:hover {
      transform: scale(1.08);
    }
  }

  .speed-btn {
    font-size: 12px;
    color: #555;
    cursor: pointer;
    background: #f0f2f5;
    border-radius: 4px;
    padding: 2px 6px;
    transition: background 0.2s, color 0.2s;

    &:hover {
      background: #09C0D7;
      color: #fff;
    }
  }
}

// -------- 播放列表 --------
.player-playlist {
  border-top: 1px solid #f0f0f0;
  max-height: none;
  display: flex;
  flex-direction: column;
}

.playlist-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  font-size: 13px;
  color: #666;
  background: #fafafa;
  flex-shrink: 0;

  .playlist-count {
    color: #999;
    font-size: 12px;
  }
}

.playlist-body {
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
}

.playlist-item {
  display: flex;
  align-items: center;
  padding: 9px 16px;
  cursor: pointer;
  transition: background 0.15s;
  gap: 10px;

  &:hover {
    background: #f5f5f5;
  }

  &.active {
    color: #09C0D7;
    background: #e6fafb;

    .item-index {
      color: #09C0D7;
    }
  }

  .item-index {
    font-size: 12px;
    color: #bbb;
    width: 20px;
    text-align: right;
    flex-shrink: 0;
  }

  .item-title {
    flex: 1;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .item-playing {
    font-size: 14px;
    color: #09C0D7;
    flex-shrink: 0;
  }
}

// -------- 列表展开动画 --------
.list-slide-enter-active,
.list-slide-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}
.list-slide-enter,
.list-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

/deep/.ant-slider-track{
  background-color: #09C0D7 !important;
}
/deep/.ant-slider-handle{
  border-color: #09C0D7 !important;
}
</style>